import { useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { type CoverageInsuranceQuery, InsuranceCheckField, InsuranceCheckFieldReason, InsuranceCheckProblem } from "@medbillai/generated-graphql-types";
import { Body3, Button, Card, Display4, YStack } from "@medbillai/ui";
import { normalizeMemberId } from "@medbillai/utils/memberId";
import { analyticsService } from "../../../lib/analytics";
import { captureUnreportedError } from "../../../lib/apollo/error";
import { errorHandling } from "../../../lib/apollo/utils";
import { useInsuranceCheckPoll } from "../../../lib/insurance/useInsuranceCheckPoll";
import { capitalizeFirstLetter } from "../../../lib/strings";
import { MemberIdInput } from "../../onboarding-v3/MemberId";
import { recordInsuranceMutation } from "../../onboarding-v3/utils/queries";
import { type SavedDetails, useCoveragePlanStore } from "../coveragePlanStore";
import { coverageInsuranceQuery } from "../read/queries";
import type { CheckFieldIssue, CoveragePlan } from "../read/readCoverage";
import { coverageSetupIdentityQuery } from "../setup/queries";
import { ConfirmedField } from "./ConfirmedField";
import { DobField, NameFields, useIdentityForm } from "./IdentityForm";
import { askingDetail, SetupChecking } from "./PlanSetupForm";
import { SetupIllustration } from "./SetupIllustration";
import type { Name } from "./usePlanSetup";
export type DetailsFix = "memberId" | "notFound" | "dateOfBirth";

/** What the user can fix about a failed check; null for a fault on the payer's side or ours. */
export const detailsFix = (problem: InsuranceCheckProblem | null): DetailsFix | null => {
  switch (problem) {
    case InsuranceCheckProblem.MEMBER_ID_REQUIRED:
      return "memberId";
    case InsuranceCheckProblem.MEMBER_NOT_FOUND:
      return "notFound";
    case InsuranceCheckProblem.DATE_OF_BIRTH_MISMATCH:
      return "dateOfBirth";
    default:
      return null;
  }
};
const copy = (fix: DetailsFix, payer: string) => {
  switch (fix) {
    case "memberId":
      return {
        title: `${payer} needs your member ID`,
        body: "It’s on the front of your insurance card."
      };
    case "notFound":
      return {
        title: "Confirm your details",
        body: `${payer} couldn’t find a plan with these details. Make sure they match your insurance card, then save.`
      };
    case "dateOfBirth":
      return {
        title: "We couldn’t confirm your plan",
        body: `${payer} has a different date of birth on file. Make sure it matches your insurance card.`
      };
  }
};
type Detail = "name" | "dateOfBirth" | "memberId";
const DETAILS: Detail[] = ["name", "dateOfBirth", "memberId"];
const NOUN: Record<Detail, string> = {
  name: "name",
  dateOfBirth: "date of birth",
  memberId: "member ID"
};
const listed = (details: Detail[]) => details.map(d => NOUN[d]).join(", ").replace(/, ([^,]*)$/, " and $1");
const detailOf = (field: InsuranceCheckField): Detail | null => {
  switch (field) {
    case InsuranceCheckField.NAME:
      return "name";
    case InsuranceCheckField.DATE_OF_BIRTH:
      return "dateOfBirth";
    case InsuranceCheckField.MEMBER_ID:
      return "memberId";
    default:
      return null;
  }
};
type Flagged = {
  details: Detail[];
  title: string;
  body: string;
};
const flaggedCopy = (issues: CheckFieldIssue[], details: Detail[], payer: string) => {
  const what = details.join() === "memberId" ? "your member ID" : `the ${listed(details)}`;
  if (issues.every(i => i.reason === InsuranceCheckFieldReason.MISSING)) {
    return {
      title: `Add ${what}`,
      body: `${payer} needs ${what} to find your plan.`
    };
  }
  const [only] = issues.length === 1 ? issues : [];
  return {
    title: `Check ${what}`,
    body: !only ? `${payer} couldn’t match ${what}. Make sure they match your insurance card.` : only.reason === InsuranceCheckFieldReason.INVALID ? `${payer} didn’t recognize that ${listed(details)}. Make sure it matches your insurance card.` : `${payer} has a different ${listed(details)} on file. Make sure it matches your insurance card.`
  };
};
export const flaggedDetails = (plan: CoveragePlan, fix: DetailsFix): Flagged | null => {
  const issues = plan.checkIssue ?? [];
  if (!plan.policyholderSelf) {
    return null;
  }
  if (issues.length === 0) {
    return fix === "notFound" && !plan.memberId ? {
      details: ["memberId"],
      title: "Add your member ID",
      body: `${plan.payerName} couldn’t find a plan with your name and date of birth. Your member ID is on the front of your insurance card.`
    } : null;
  }
  const flagged = issues.map(i => detailOf(i.field));
  if (flagged.includes(null) || issues.some(i => i.reason === InsuranceCheckFieldReason.UNRECOGNIZED)) {
    return null;
  }
  const details = DETAILS.filter(d => flagged.includes(d));
  return {
    details,
    ...flaggedCopy(issues, details, plan.payerName)
  };
};

/** A save that asks for a check stamps the request within a few seconds. */
const REQUEST_WINDOW_MS = 10_000;
/** Past the API's 5 s hold and the check itself. */
const RECHECK_WINDOW_MS = 30_000;
const timeOf = (d: Date | null | undefined) => d?.getTime() ?? 0;

/**
 * Waits on the check a save requests: its attempt clears the save, so the card
 * shows the result. A save that requests nothing (identical details), or a
 * check that never runs in time, leaves the saved copy.
 */
export function DetailsRecheck({
  since
}: {
  since: number;
}) {
  const markDetailsSaved = useCoveragePlanStore(state => state.markDetailsSaved);
  const stopChecking = useCoveragePlanStore(state => state.stopChecking);
  const requested = ({
    myInsurance
  }: CoverageInsuranceQuery) => timeOf(myInsurance.checkStatus.lastRequestedAt) > since;
  const attempted = (d: CoverageInsuranceQuery) => {
    const {
      lastRequestedAt,
      lastAttemptAt
    } = d.myInsurance.checkStatus;
    return requested(d) && timeOf(lastAttemptAt) > timeOf(lastRequestedAt);
  };
  const {
    data
  } = useInsuranceCheckPoll(coverageInsuranceQuery, {
    polling: true,
    landed: attempted
  });
  const landed = data !== undefined && attempted(data);
  const asked = data !== undefined && requested(data);
  useEffect(() => {
    if (landed) {
      markDetailsSaved(undefined);
    }
  }, [landed, markDetailsSaved]);
  useEffect(() => {
    const timer = setTimeout(stopChecking, since + (asked ? RECHECK_WINDOW_MS : REQUEST_WINDOW_MS) - Date.now());
    return () => clearTimeout(timer);
  }, [since, asked, stopChecking]);
  return null;
}
export function DetailsChecking({
  payerName
}: {
  payerName: string;
}) {
  return <Card variant="raised" size="large" padding="$sp2" data-sentry-element="Card" data-sentry-component="DetailsChecking" data-sentry-source-file="FixDetailsCard.tsx">
      <SetupIllustration data-sentry-element="SetupIllustration" data-sentry-source-file="FixDetailsCard.tsx" />
      <SetupChecking title="Checking your plan…" detail={askingDetail(payerName)} data-sentry-element="SetupChecking" data-sentry-source-file="FixDetailsCard.tsx" />
    </Card>;
}

/** A never-confirmed plan's details, fixed in place; saving them starts a check. */
export function FixDetailsCard({
  plan,
  fix,
  onTryAnotherPlan
}: {
  plan: CoveragePlan;
  fix: DetailsFix;
  onTryAnotherPlan: () => void;
}) {
  const {
    data,
    error,
    refetch
  } = useQuery(coverageSetupIdentityQuery, {
    context: errorHandling("caller")
  });
  const saved = useCoveragePlanStore(state => state.detailsSaved === plan.membershipId);
  const markDetailsSaved = useCoveragePlanStore(state => state.markDetailsSaved);
  const flagged = flaggedDetails(plan, fix);
  const {
    title,
    body
  } = flagged ?? copy(fix, plan.payerName);
  return <Card variant="raised" size="large" padding="$sp2" gap="$sp1.5" data-sentry-element="Card" data-sentry-component="FixDetailsCard" data-sentry-source-file="FixDetailsCard.tsx">
      <Display4 color="$textDefault" data-sentry-element="Display4" data-sentry-source-file="FixDetailsCard.tsx">
        {saved ? "Thanks, we’re on it" : title}
      </Display4>
      {saved ? <>
          <Body3 color="$textSubtle">
            {`We’ll check your plan with ${plan.payerName} again in the next few days.`}
          </Body3>
          <CardActions primary="Edit details" onPrimary={() => markDetailsSaved(undefined)} onTryAnotherPlan={onTryAnotherPlan} />
        </> : <>
          <Body3 color="$textSubtle">{body}</Body3>
          {/* Mounted once the profile loads: the form reads its defaults once. */}
          {data ? <FixDetailsForm plan={plan} fix={fix} flagged={flagged?.details ?? null} person={data.me?.primaryPerson ?? null} key={plan.membershipId} onSaved={details => markDetailsSaved(plan.membershipId, details)} onTryAnotherPlan={onTryAnotherPlan} /> : error ? <>
              <Body3 color="$textNegative" role="alert">
                We couldn’t load your details.
              </Body3>
              <CardActions primary="Try again" onPrimary={() => void refetch().catch(() => {})} onTryAnotherPlan={onTryAnotherPlan} />
            </> : null}
        </>}
    </Card>;
}
function FixDetailsForm({
  plan,
  fix,
  flagged,
  person,
  onSaved,
  onTryAnotherPlan
}: {
  plan: CoveragePlan;
  fix: DetailsFix;
  flagged: Detail[] | null;
  person: {
    givenNames?: string[] | null;
    familyName?: string | null;
    dateOfBirth?: string | null;
  } | null;
  onSaved: (details: SavedDetails) => void;
  onTryAnotherPlan: () => void;
}) {
  const onFileName = person?.givenNames?.length && person.familyName ? {
    firstName: person.givenNames.join(" "),
    lastName: person.familyName
  } : null;
  const onFileDob = person?.dateOfBirth || null;
  const askName = fix === "notFound" || !onFileName;
  const askDob = fix !== "memberId" || !onFileDob;
  const askId = flagged !== null || fix !== "dateOfBirth";
  const requireId = fix === "memberId" || flagged?.includes("memberId");
  const saved = useCoveragePlanStore(state => state.savedDetails?.membershipId === plan.membershipId ? state.savedDetails : undefined);
  const [memberId, setMemberId] = useState(saved?.memberId ?? plan.memberId ?? "");
  const [rejected, setRejected] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [recordInsurance] = useMutation(recordInsuranceMutation);
  const save = (name: Name | null, dateOfBirth: string) => {
    const typed = memberId.trim();
    const normalized = askId && typed ? normalizeMemberId(typed) : undefined;
    if (normalized === null || requireId && !typed) {
      setRejected(true);
      setExpanded(true);
      return;
    }
    setSaving(true);
    setFailed(false);
    recordInsurance({
      variables: {
        input: {
          carrier: plan.payerName,
          payerId: plan.payerId,
          memberId: normalized,
          ...(name ?? onFileName),
          dateOfBirth
        }
      }
    }).then(() => {
      analyticsService.trackEvent("CoverageFixDetailsSaved", {
        fix,
        outcome: "saved"
      });
      onSaved({
        membershipId: plan.membershipId,
        name: name ?? onFileName,
        dateOfBirth,
        memberId: normalized
      });
    }, error => {
      analyticsService.trackEvent("CoverageFixDetailsSaved", {
        fix,
        outcome: "failed"
      });
      captureUnreportedError(error);
      setFailed(true);
    }).finally(() => setSaving(false));
  };
  const initialName = saved?.name ?? onFileName;
  const initialDob = saved?.dateOfBirth ?? onFileDob;
  const identity = useIdentityForm({
    askName: flagged !== null || askName,
    initialName,
    initialDob,
    onDone: ({
      name,
      dateOfBirth
    }) => save(name, dateOfBirth)
  });
  const summaries: Record<Detail, string | null> = {
    name: initialName ? `${initialName.firstName} ${initialName.lastName}` : null,
    dateOfBirth: initialDob ? dayjs(initialDob).format("MMM D, YYYY") : null,
    memberId: saved?.memberId ?? plan.memberId ?? null
  };
  const shown = (detail: Detail) => flagged ? flagged.includes(detail) || expanded || !summaries[detail] : {
    name: askName,
    dateOfBirth: askName || askDob,
    memberId: askId
  }[detail];
  const order = flagged ? [...flagged, ...DETAILS.filter(d => !flagged.includes(d))] : DETAILS;
  const confirmed = DETAILS.filter(d => !shown(d));
  return <YStack gap="$sp1" data-sentry-element="YStack" data-sentry-component="FixDetailsForm" data-sentry-source-file="FixDetailsCard.tsx">
      {order.filter(shown).map(detail => detail === "name" ? <NameFields key={detail} identity={identity} /> : detail === "dateOfBirth" ? <DobField key={detail} identity={identity} /> : <MemberIdInput key={detail} value={memberId} onChangeText={value => {
      setMemberId(value);
      setRejected(false);
    }} rejected={rejected} />)}
      {flagged && confirmed.length > 0 ? <ConfirmedField label={capitalizeFirstLetter(listed(confirmed))} value={confirmed.map(d => summaries[d]).join(" · ")} editLabel={`Edit ${listed(confirmed)}`} onEdit={() => setExpanded(true)} /> : null}
      {failed ? <Body3 color="$textNegative" role="alert">
          Something went wrong, please try again.
        </Body3> : null}
      <YStack gap="$sp0.5" marginTop="$sp0.5" data-sentry-element="YStack" data-sentry-source-file="FixDetailsCard.tsx">
        <Button variant="brand" loading={saving} onPress={() => flagged || askName || askDob ? identity.submit() : save(onFileName, onFileDob ?? "")} data-sentry-element="Button" data-sentry-source-file="FixDetailsCard.tsx">
          Save
        </Button>
        <Button variant="tertiary" onPress={onTryAnotherPlan} data-sentry-element="Button" data-sentry-source-file="FixDetailsCard.tsx">
          Try another plan
        </Button>
      </YStack>
    </YStack>;
}
function CardActions({
  primary,
  onPrimary,
  onTryAnotherPlan
}: {
  primary: string;
  onPrimary: () => void;
  onTryAnotherPlan: () => void;
}) {
  return <YStack gap="$sp0.5" data-sentry-element="YStack" data-sentry-component="CardActions" data-sentry-source-file="FixDetailsCard.tsx">
      <Button variant="secondary" onPress={onPrimary} data-sentry-element="Button" data-sentry-source-file="FixDetailsCard.tsx">
        {primary}
      </Button>
      <Button variant="tertiary" onPress={onTryAnotherPlan} data-sentry-element="Button" data-sentry-source-file="FixDetailsCard.tsx">
        Try another plan
      </Button>
    </YStack>;
}